import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="bg-white rounded-3xl p-10 max-w-md w-full shadow-xl border border-slate-100">
        <Logo />
        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password/request-reset", { email });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <Shell>
      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <MailCheck className="mx-auto text-emerald-500" size={48} />
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">Verifique seu e-mail</h1>
          <p className="mt-2 text-slate-600">
            Se este e-mail existir no AulaPronta, enviamos um link para redefinir sua senha. O link é válido por 30 minutos.
          </p>
          <Link to="/login"><Button className="mt-6 rounded-full bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="back-login">Voltar ao login</Button></Link>
        </div>
      ) : (
        <>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Esqueci minha senha</h1>
          <p className="mt-2 text-slate-600">Digite seu e-mail e enviaremos um link para redefinir.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">E-mail</Label>
              <Input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                className="rounded-xl h-12" placeholder="voce@escola.com" data-testid="input-email"/>
            </div>
            <Button disabled={loading} className="w-full rounded-full h-12 bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="btn-request-reset">
              {loading && <Loader2 className="animate-spin mr-2" size={16}/>} Enviar link de redefinição
            </Button>
          </form>
          <div className="mt-6 text-sm text-slate-600">
            Lembrou? <Link to="/login" className="font-semibold text-[#8B5CF6]">Entrar</Link>
          </div>
        </>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("As senhas não coincidem."); return; }
    setLoading(true);
    try {
      await api.post("/auth/password/confirm-reset", { token, new_password: pw });
      toast.success("Senha redefinida! Faça login com a nova senha.");
      nav("/login");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (!token) return (
    <Shell>
      <h1 className="font-heading text-2xl font-extrabold text-slate-900">Link inválido</h1>
      <p className="mt-2 text-slate-600">O link de redefinição parece incompleto. Solicite outro.</p>
      <Link to="/forgot-password"><Button className="mt-4 rounded-full bg-[#1F3A8A]">Solicitar novo link</Button></Link>
    </Shell>
  );

  return (
    <Shell>
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Nova senha</h1>
      <p className="mt-2 text-slate-600">Escolha uma senha nova para sua conta.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Nova senha</Label>
          <Input type="password" required minLength={6} value={pw} onChange={e => setPw(e.target.value)}
            className="rounded-xl h-12" placeholder="Mínimo 6 caracteres" data-testid="input-new-password"/>
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Repita a nova senha</Label>
          <Input type="password" required minLength={6} value={pw2} onChange={e => setPw2(e.target.value)}
            className="rounded-xl h-12" placeholder="Confirme" data-testid="input-new-password-2"/>
        </div>
        <Button disabled={loading} className="w-full rounded-full h-12 bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="btn-confirm-reset">
          {loading && <Loader2 className="animate-spin mr-2" size={16}/>} Redefinir senha
        </Button>
      </form>
    </Shell>
  );
}
